import { createContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  type User,
  type OAuthCredential,
} from 'firebase/auth';
import { auth } from '../config/firebase';

const STORAGE_KEY = 'faana_portal_google_token';
const STORAGE_EXPIRY_KEY = 'faana_portal_google_token_expiry';

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  googleAccessToken: string | null;
  getGoogleAccessToken: () => Promise<string | null>;
  login: () => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const PortalAuthContext = createContext<AuthState | null>(null);

function storeToken(token: string) {
  sessionStorage.setItem(STORAGE_KEY, token);
  // Google tokens expire in 1 hour, store expiry at 50 minutes for safety
  sessionStorage.setItem(STORAGE_EXPIRY_KEY, String(Date.now() + 50 * 60 * 1000));
}

function getStoredToken(): string | null {
  const token = sessionStorage.getItem(STORAGE_KEY);
  const expiry = sessionStorage.getItem(STORAGE_EXPIRY_KEY);
  if (!token || !expiry) return null;
  if (Date.now() > Number(expiry)) {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_EXPIRY_KEY);
    return null;
  }
  return token;
}

function clearStoredToken() {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_EXPIRY_KEY);
}

async function ensureSessionCookie(user: User): Promise<void> {
  const hasSession = document.cookie.split(';').some(c => c.trim().startsWith('__session='));
  if (hasSession) return;

  const idToken = await user.getIdToken();
  await fetch('/api/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
}

function buildProvider(): GoogleAuthProvider {
  // Basic scopes only (openid/email/profile). drive.readonly is a Google
  // "restricted" scope: requesting it at sign-in throws the "Google hasn't
  // verified this app" wall in front of every external (non-faanaworks)
  // user. Drive card metadata degrades silently without it (useDriveMetadata
  // bails on 403). The hd hint is gone for the same reason: it prefills
  // @faanaworks.com and confuses external invitees.
  return new GoogleAuthProvider();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(getStoredToken);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setIsLoading(false);

      if (firebaseUser) {
        await ensureSessionCookie(firebaseUser);
      } else {
        clearStoredToken();
        setGoogleAccessToken(null);
      }
    });
    return unsubscribe;
  }, []);

  const login = useCallback(async () => {
    const result = await signInWithPopup(auth, buildProvider());
    const credential = GoogleAuthProvider.credentialFromResult(result) as OAuthCredential | null;
    if (credential?.accessToken) {
      storeToken(credential.accessToken);
      setGoogleAccessToken(credential.accessToken);
    }
    await ensureSessionCookie(result.user);
  }, []);

  // Email/password sign-in for external invitees whose accounts are
  // provisioned by an admin (client-side sign-UP is disabled project-wide).
  const loginWithPassword = useCallback(async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    await ensureSessionCookie(result.user);
  }, []);

  // Get the Google access token if available. Returns null if expired - no popup.
  const getGoogleAccessToken = useCallback(async (): Promise<string | null> => {
    return getStoredToken();
  }, []);

  const logout = useCallback(async () => {
    await fetch('/api/session/out', { method: 'POST' }).catch(() => {});
    await signOut(auth);
    clearStoredToken();
    setGoogleAccessToken(null);
  }, []);

  return (
    <PortalAuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        googleAccessToken,
        getGoogleAccessToken,
        login,
        loginWithPassword,
        logout,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}
